/**
 * Send a print job to the device.
 *
 * Browsers: the job is placed in a print-only area of the current page and `window.print()` is
 * called once its label images have decoded, so no pop-up blocker or new tab is involved.
 * Native shells (Fieldbook iOS, smartforms): each label is rasterised at the printer's resolution
 * and handed to the bridge's `printImage`.
 */

const PRINT_AREA_ID = 'HeadlightQR-PrintArea';
const PRINT_STYLE_ID = 'HeadlightQR-PrintStyle';

/**
 * @param {Document} pDocument
 * @return {HTMLElement}
 */
function ensurePrintArea(pDocument)
{
	let tmpArea = pDocument.getElementById(PRINT_AREA_ID);
	if (!tmpArea)
	{
		tmpArea = pDocument.createElement('div');
		tmpArea.id = PRINT_AREA_ID;
		tmpArea.setAttribute('aria-hidden', 'true');
		pDocument.body.appendChild(tmpArea);
	}
	return tmpArea;
}

/**
 * @param {Document} pDocument
 * @param {string} pPageCSS - The `@page` rule for this job.
 */
function setPrintStyle(pDocument, pPageCSS)
{
	let tmpStyle = pDocument.getElementById(PRINT_STYLE_ID);
	if (!tmpStyle)
	{
		tmpStyle = pDocument.createElement('style');
		tmpStyle.id = PRINT_STYLE_ID;
		pDocument.head.appendChild(tmpStyle);
	}
	tmpStyle.textContent = `
@media screen { #${PRINT_AREA_ID} { display: none; } }
@media print
{
	html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; min-width: 0 !important; }
	body > *:not(#${PRINT_AREA_ID}) { display: none !important; }
	#${PRINT_AREA_ID} { display: block !important; }
	#${PRINT_AREA_ID}, #${PRINT_AREA_ID} * { visibility: visible !important; }
	#${PRINT_AREA_ID} * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}
${pPageCSS}`;
}

/**
 * Resolve once every image in the element has loaded and decoded (or failed).
 *
 * Labels are `<img src="data:...">`. A freshly assigned src is decoded asynchronously, so printing straight
 * after `innerHTML` snapshots an empty image the first time; the second print only works because the browser
 * has cached the decoded image by then.
 *
 * @param {HTMLElement} pElement
 * @return {Promise<void>}
 */
function waitForImages(pElement)
{
	if (!pElement || typeof pElement.querySelectorAll !== 'function')
	{
		return Promise.resolve();
	}
	const tmpImages = Array.from(pElement.querySelectorAll('img'));
	return Promise.all(tmpImages.map((pImage) =>
	{
		if (typeof pImage.decode === 'function')
		{
			// A broken image rejects; print what we have rather than nothing.
			return pImage.decode().catch(() => undefined);
		}
		if (pImage.complete)
		{
			return undefined;
		}
		return new Promise((fResolve) =>
		{
			pImage.addEventListener('load', fResolve, { once: true });
			pImage.addEventListener('error', fResolve, { once: true });
		});
	})).then(() => undefined);
}

/**
 * Print HTML labels through the browser, once the label images are ready to paint.
 *
 * @param {string} pHTML - Output of Label-Renderer.renderPrintJob.
 * @param {string} pPageCSS
 * @param {Window} [pWindow]
 * @return {Promise<void>}
 */
async function printHTML(pHTML, pPageCSS, pWindow)
{
	const tmpWindow = pWindow || window;
	const tmpDocument = tmpWindow.document;
	setPrintStyle(tmpDocument, pPageCSS);
	const tmpArea = ensurePrintArea(tmpDocument);
	tmpArea.innerHTML = pHTML;
	await waitForImages(tmpArea);
	tmpWindow.print();
}

/**
 * Print rasterised labels through a native bridge that implements `printImage`.
 *
 * @param {{ printImage: (pOptions: { jobName: string, base64Image: string }) => Promise<any> }} pBridge
 * @param {Array<string>} pImages - Data URLs, one per label.
 * @param {string} [pJobName]
 * @return {Promise<Array<any>>}
 */
async function printNative(pBridge, pImages, pJobName)
{
	const tmpResults = [];
	for (const tmpImage of pImages)
	{
		tmpResults.push(await pBridge.printImage({ jobName: pJobName || 'HeadLight label', base64Image: tmpImage }));
	}
	return tmpResults;
}

module.exports =
{
	PRINT_AREA_ID,
	printHTML,
	printNative,
};
